import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Users, ArrowUp, Sparkles, X, Copy, Flag, Ban, LogOut, UserMinus, Crown,
  Plus, FileText, Zap, Trophy, Mic, SmilePlus, Play, Loader2, RotateCw,
  Share2, Link2, Check, Clock,
} from "lucide-react";
import { useAuth } from "@/auth";
import { apiFetch, BACKEND_ORIGIN } from "@/api";
import { Button, Field, Spinner, cn } from "@/ui";
import { VoiceRecordingBar } from "@/components/VoiceRecordingBar";

const EMOJIS = ["👍", "❤️", "😂", "🎉"];

function clean(t) {
  return (t || "").replace(/\*\*(.*?)\*\*/g, "$1").replace(/\*(.*?)\*/g, "$1").replace(/^#{1,6}\s+/gm, "").replace(/`{1,3}/g, "").trim();
}
function fileUrl(path, token) {
  return `${process.env.REACT_APP_BACKEND_URL}/api/social/files/${path}?t=${token || ""}`;
}

export default function SocialRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { account } = useAuth();
  const me = account?.id;
  const [room, setRoom] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [playingId, setPlayingId] = useState(null);
  const [panel, setPanel] = useState(false);
  const [mediaToken, setMediaToken] = useState("");
  const [reportFor, setReportFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [quizModal, setQuizModal] = useState(false);
  const [quizTopic, setQuizTopic] = useState("");
  const [quizBusy, setQuizBusy] = useState(false);
  const [quizCount, setQuizCount] = useState(5);
  const [quizStates, setQuizStates] = useState({});
  const [leaderOpen, setLeaderOpen] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [profileFor, setProfileFor] = useState(null);
  const scrollRef = useRef(null);
  const lastAt = useRef(null);
  const imgRef = useRef(null);
  const docRef = useRef(null);
  const mediaRef = useRef(null);
  const audioRef = useRef(null);
  const cancelRef = useRef(false);
  const scrollModeRef = useRef("bottom");
  const pinIdRef = useRef(null);
  const recTimerRef = useRef(null);
  const roomRef = useRef(null);
  useEffect(() => { roomRef.current = room; }, [room]);

  const scrollEnd = () => setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 60);
  const pinTop = () => setTimeout(() => {
    const c = scrollRef.current;
    const el = pinIdRef.current && document.getElementById("rm-" + pinIdRef.current);
    if (!c || !el) return;
    c.scrollTop += el.getBoundingClientRect().top - c.getBoundingClientRect().top - 12;
  }, 60);
  const nameFor = (id) => (id === me ? "You" : members.find((x) => x.account_id === id)?.name || "Someone");

  const mergeMsgs = useCallback((incoming) => {
    if (!incoming?.length) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      const next = [...prev, ...incoming.filter((m) => m && !seen.has(m.id))];
      lastAt.current = next[next.length - 1]?.created_at || lastAt.current;
      return next;
    });
  }, []);

  const loadRoom = useCallback(async () => {
    try {
      const d = await apiFetch(`/social/rooms/${roomId}`);
      setRoom(d.room); setMembers(d.members || []);
      try {
        const mt = await apiFetch(`/social/rooms/${roomId}/media-token`, { method: "POST" });
        setMediaToken(mt.token);
      } catch {}
    } catch (e) {
      if (e.status === 402) return navigate("/paywall");
      toast(e.message); navigate("/social");
    }
  }, [roomId, navigate]);

  const loadMessages = useCallback(async () => {
    try {
      const q = lastAt.current ? `?after=${encodeURIComponent(lastAt.current)}` : "";
      const d = await apiFetch(`/social/rooms/${roomId}/messages${q}`);
      mergeMsgs(d.messages || []);
    } catch {}
  }, [roomId, mergeMsgs]);

  const loadReactions = useCallback(async () => {
    try {
      const d = await apiFetch(`/social/rooms/${roomId}/reactions`);
      const map = d.reactions || {};
      setMessages((prev) => prev.map((m) => ({ ...m, reactions: map[m.id] || {} })));
      setDetailFor((prev) => (prev ? { ...prev, reactions: map[prev.id] || {} } : prev));
    } catch {}
  }, [roomId]);

  const loadQuizState = useCallback(async (sid) => {
    if (!sid) return;
    try { const d = await apiFetch(`/quiz/${sid}/state`); setQuizStates((p) => ({ ...p, [sid]: d })); } catch {}
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); await loadRoom(); await loadMessages(); await loadReactions(); setLoading(false); scrollEnd(); })();
  }, [loadRoom, loadMessages, loadReactions]);
  const pollRoomState = useCallback(async () => {
    try { const d = await apiFetch(`/social/rooms/${roomId}`); setRoom(d.room); setMembers(d.members || []); } catch {}
  }, [roomId]);
  useEffect(() => {
    const t = setInterval(() => {
      loadMessages(); loadReactions(); pollRoomState();
      const sid = roomRef.current?.active_quiz_session_id;
      if (sid) loadQuizState(sid);
    }, 3000);
    return () => clearInterval(t);
  }, [loadMessages, loadReactions, pollRoomState, loadQuizState]);
  // Load state for any quiz game present in the feed (handles reload + completed games).
  useEffect(() => {
    const ids = new Set(messages.filter((m) => m.kind === "quiz_question").map((m) => m.quiz?.session_id).filter(Boolean));
    ids.forEach((sid) => { if (!quizStates[sid]) loadQuizState(sid); });
  }, [messages, quizStates, loadQuizState]);
  useEffect(() => {
    // Media tokens are short-lived (20 min); refresh so images keep loading in long-open rooms.
    const t = setInterval(async () => {
      try { const mt = await apiFetch(`/social/rooms/${roomId}/media-token`, { method: "POST" }); setMediaToken(mt.token); } catch {}
    }, 15 * 60 * 1000);
    return () => clearInterval(t);
  }, [roomId]);
  useEffect(() => {
    const c = scrollRef.current;
    if (scrollModeRef.current === "top") { scrollModeRef.current = "bottom"; pinTop(); }
    else if (c && c.scrollHeight - c.scrollTop - c.clientHeight < 250) { scrollEnd(); }
  }, [messages.length]);
  // During a live quiz, always jump to the newest question card when it drops.
  const quizCardCount = messages.filter((m) => m.kind === "quiz_question").length;
  useEffect(() => { if (quizCardCount > 0) scrollEnd(); }, [quizCardCount]);

  const uploadFiles = async (files) => {
    setUploading(true);
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) { toast(`${f.name} is too large (max 15MB).`); continue; }
      const form = new FormData(); form.append("file", f);
      try {
        const d = await apiFetch(`/social/rooms/${roomId}/upload`, { method: "POST", form });
        setAttachments((p) => [...p, { ...d, previewUrl: d.type === "image" ? URL.createObjectURL(f) : null }]);
      } catch (e) { toast(e.message || "Upload failed."); }
    }
    setUploading(false);
  };
  const onPickImages = (e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ""; };
  const onPickDocs = (e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ""; };

  const postMessage = async (body, atts, viaVoice = false) => {
    try {
      const d = await apiFetch(`/social/rooms/${roomId}/messages`, { method: "POST", body: { text: body, attachments: atts, via_voice: viaVoice } });
      const add = [d.message]; if (d.maxx_message) add.push(d.maxx_message);
      if (d.maxx_message) { pinIdRef.current = d.message?.id || d.maxx_message?.id; scrollModeRef.current = "top"; }
      mergeMsgs(add);
      if (!d.maxx_message) scrollEnd();
      if (d.maxx_throttled) toast("MAXx is catching its breath — the group asked a lot this minute. Try again shortly.");
      return true;
    } catch (e) {
      if (e.status === 402) { navigate("/paywall"); return false; }
      toast(e.message); return false;
    }
  };

  const send = async () => {
    const body = text.trim();
    if ((!body && attachments.length === 0) || sending) return;
    const sent = attachments.map((a) => ({ type: a.type, name: a.name, storage_path: a.storage_path }));
    setSending(true); setText(""); setAttachments([]);
    const ok = await postMessage(body, sent);
    if (!ok) setText(body);
    setSending(false);
  };

  const askMaxx = async () => {
    if (sending) return;
    const body = text.trim(); setText(""); setSending(true);
    try {
      const d = await apiFetch(`/social/rooms/${roomId}/ask-maxx`, { method: "POST", body: { text: body } });
      const add = []; if (d.message) add.push(d.message); if (d.maxx_message) add.push(d.maxx_message);
      if (d.maxx_message) { pinIdRef.current = d.message?.id || d.maxx_message?.id; scrollModeRef.current = "top"; }
      mergeMsgs(add);
      if (!d.maxx_message) scrollEnd();
      if (d.maxx_throttled) toast("MAXx is catching its breath — the group asked a lot this minute. Try again shortly.");
    } catch (e) {
      if (e.status === 402) return navigate("/paywall");
      toast(e.message);
    } finally { setSending(false); }
  };

  const startVoice = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (cancelRef.current) { cancelRef.current = false; return; }
        const form = new FormData();
        form.append("file", new Blob(chunks, { type: "audio/webm" }), "audio.webm");
        try {
          const d = await apiFetch("/user/voice/transcribe", { method: "POST", form });
          if (d.text?.trim()) setText((prev) => (prev ? prev.trimEnd() + " " : "") + d.text.trim());
          else toast("Didn't catch that. Try again.");
        } catch { toast("Voice failed. Try again."); }
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
      setRecSecs(0); clearInterval(recTimerRef.current); recTimerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch { toast("Microphone permission is needed for voice."); }
  };
  const stopVoice = () => { setRecording(false); clearInterval(recTimerRef.current); mediaRef.current?.stop(); };
  const cancelVoice = () => { cancelRef.current = true; setRecording(false); clearInterval(recTimerRef.current); mediaRef.current?.stop(); };

  const playVoice = async (m) => {
    if (playingId) { audioRef.current?.pause(); }
    setPlayingId(m.id);
    try {
      const d = await apiFetch("/user/voice/speak", { method: "POST", body: { text: m.text } });
      if (!d.audio_base64) { toast("Couldn't play that."); setPlayingId(null); return; }
      const audio = new Audio(`data:audio/mp3;base64,${d.audio_base64}`);
      audioRef.current = audio;
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => setPlayingId(null);
      await audio.play();
    } catch { toast("Couldn't play that."); setPlayingId(null); }
  };

  const toggleReaction = async (m, emoji) => {
    setMessages((prev) => prev.map((x) => {
      if (x.id !== m.id) return x;
      const rx = { ...(x.reactions || {}) };
      const arr = rx[emoji] || [];
      const next = arr.includes(me) ? arr.filter((u) => u !== me) : [...arr, me];
      if (next.length) rx[emoji] = next; else delete rx[emoji];
      return { ...x, reactions: rx };
    }));
    try {
      const d = await apiFetch(`/social/rooms/${roomId}/messages/${m.id}/react`, { method: "POST", body: { emoji } });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, reactions: d.reactions } : x)));
    } catch (e) { toast(e.message); }
  };

  const toggleMaxx = async () => {
    try { const path = room?.maxx_present ? "maxx/remove" : "maxx"; const d = await apiFetch(`/social/rooms/${roomId}/${path}`, { method: "POST" }); setRoom(d.room); loadMessages(); } catch (e) { toast(e.message); }
  };
  const startQuiz = async () => {
    if (quizBusy) return;
    setQuizBusy(true);
    try {
      const res = await apiFetch("/quiz/start", { method: "POST", body: { mode: "group", room_id: roomId, topic: quizTopic.trim(), count: quizCount } });
      setQuizModal(false); setQuizTopic(""); setPanel(false);
      setRoom((r) => (r ? { ...r, active_quiz_session_id: res.session_id, active_quiz_topic: res.topic } : r));
      await loadMessages();
      loadQuizState(res.session_id);
      scrollEnd();
    } catch (e) {
      if (e.status === 402) return navigate("/paywall");
      toast(e.message || "Couldn't start the quiz.");
    } finally { setQuizBusy(false); }
  };
  const answerQuiz = async (sessionId, qIndex, choiceIndex) => {
    if (!sessionId) return;
    setQuizStates((p) => (p[sessionId] ? { ...p, [sessionId]: { ...p[sessionId], my_answers: { ...p[sessionId].my_answers, [qIndex]: choiceIndex } } } : p));
    try { await apiFetch(`/quiz/${sessionId}/group-answer`, { method: "POST", body: { q_index: qIndex, choice_index: choiceIndex } }); }
    catch (e) { if (e.status !== 409) toast(e.message || "Couldn't submit that answer."); }
    finally { loadQuizState(sessionId); loadMessages(); }
  };
  const openLeaderboard = async () => {
    setPanel(false);
    let rows = [];
    try { const d = await apiFetch(`/social/rooms/${roomId}/leaderboard`); rows = d.leaderboard || []; }
    catch (e) { toast(e.message); }
    setLeaderboard(rows); setLeaderOpen(true);
  };
  const openProfile = async (accountId) => {
    if (!accountId || accountId === "maxx") return;
    setPanel(false);
    setProfileFor({ loading: true });
    try { const d = await apiFetch(`/social/rooms/${roomId}/members/${accountId}`); setProfileFor(d); }
    catch (e) { toast(e.message); setProfileFor(null); }
  };
  const onQuizStartClick = () => {
    setPanel(false);
    if (room?.active_quiz_session_id) { toast("A 21 Questions game is in progress below."); scrollEnd(); }
    else setQuizModal(true);
  };

  const inviteUrl = room?.invite_url || (room?.invite_token ? `${window.location.origin}/social/join/${room.invite_token}` : "");
  const copyInviteLink = async () => { try { await navigator.clipboard?.writeText(inviteUrl); toast("Invite link copied — paste it to a friend."); } catch { toast("Copy not available."); } };
  const shareInvite = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `Join "${room?.name}" on MAXx`, text: `Join my chat "${room?.name}" on MAXx:`, url: inviteUrl }); return; } catch { /* cancelled or unsupported */ }
    }
    copyInviteLink();
  };
  const resetInvite = async () => {
    try {
      const d = await apiFetch(`/social/rooms/${room.id}/rotate-invite`, { method: "POST" });
      setRoom((r) => ({ ...r, invite_token: d.invite_token, invite_url: d.invite_url }));
      toast("New invite link ready — the old one no longer works.");
    } catch (e) { toast(e.message); }
  };

  useEffect(() => {
    if (room && searchParams.get("invite") === "1") {
      setInviteOpen(true);
      searchParams.delete("invite");
      setSearchParams(searchParams, { replace: true });
    }
  }, [room, searchParams, setSearchParams]);
  const blockUser = async (m) => { try { await apiFetch("/social/blocks", { method: "POST", body: { user_id: m.account_id } }); toast(`Blocked ${m.name}.`); loadRoom(); } catch (e) { toast(e.message); } };
  const removeMember = async (m) => { if (!window.confirm(`Remove ${m.name} from this chat?`)) return; try { await apiFetch(`/social/rooms/${roomId}/members/${m.account_id}/remove`, { method: "POST" }); toast(`${m.name} removed.`); loadRoom(); } catch (e) { toast(e.message); } };
  const leave = async () => { if (!window.confirm("Leave this chat?")) return; try { await apiFetch(`/social/rooms/${roomId}/leave`, { method: "POST" }); navigate("/social"); } catch (e) { toast(e.message); } };
  const submitReport = async (reason) => {
    const target = reportFor;
    try { await apiFetch("/social/reports", { method: "POST", body: { target_type: target.member ? "user" : "message", target_id: target.member ? target.account_id : target.id, room_id: roomId, reason } }); toast("Thanks — this was reported for review."); } catch (e) { toast(e.message); } finally { setReportFor(null); }
  };

  const isLeader = room?.leader_id === me;

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border/70 bg-background/80 px-4 py-3 backdrop-blur-xl">
        <button data-testid="room-back" onClick={() => navigate("/social")} className="flex items-center gap-1 text-sm font-semibold"><ChevronLeft className="h-5 w-5" /></button>
        <button data-testid="room-title" onClick={() => setPanel(true)} className="flex min-w-0 flex-col items-center">
          <span className="flex items-center gap-1.5 truncate text-base font-semibold">{room?.name} {room?.maxx_present ? <Sparkles className="h-3.5 w-3.5 text-muted-foreground" /> : null}</span>
          <span className="text-xs text-muted-foreground">{members.length} {members.length === 1 ? "member" : "members"} · tap for info</span>
        </button>
        <button data-testid="room-info-btn" onClick={() => setPanel(true)} className="p-1"><Users className="h-5 w-5" strokeWidth={1.5} /></button>
      </header>

      <main ref={scrollRef} className="max-scroll relative z-10 flex-1 overflow-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-3 px-4 py-5">
          {loading ? <div className="flex justify-center py-24"><Spinner className="h-6 w-6 text-foreground" /></div>
            : messages.map((m) => (
              <div id={`rm-${m.id}`} key={m.id}>
                {m.kind === "quiz_question" ? (
                  <QuizCard
                    m={m}
                    qstate={quizStates[m.quiz?.session_id]?.questions?.[m.quiz?.q_index]}
                    isCurrent={quizStates[m.quiz?.session_id]?.current_index === m.quiz?.q_index && quizStates[m.quiz?.session_id]?.status === "active"}
                    inRoster={quizStates[m.quiz?.session_id]?.in_roster}
                    myChoice={quizStates[m.quiz?.session_id]?.my_answers?.[m.quiz?.q_index]}
                    deadline={quizStates[m.quiz?.session_id]?.question_deadline}
                    onAnswer={(ci) => answerQuiz(m.quiz?.session_id, m.quiz?.q_index, ci)}
                    onExpire={() => loadQuizState(m.quiz?.session_id)}
                  />
                ) : (
                  <RoomBubble m={m} me={me} mediaToken={mediaToken} playingId={playingId} onReport={() => setReportFor(m)} onReact={(e) => toggleReaction(m, e)} onDetails={() => setDetailFor(m)} onPlay={() => playVoice(m)} onOpenProfile={openProfile} />
                )}
              </div>
            ))}
        </div>
      </main>

      {attachments.length || uploading ? (
        <div className="relative z-10 mx-auto flex w-full max-w-2xl flex-wrap items-center gap-2 px-4 pb-2">
          {attachments.map((a, i) => (
            <div key={i} className="relative">
              {a.type === "image" && a.previewUrl ? <img src={a.previewUrl} alt="" className="h-14 w-14 rounded-lg object-cover" />
                : <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-secondary"><FileText className="h-5 w-5 text-muted-foreground" /></div>}
              <button onClick={() => setAttachments((p) => p.filter((_, idx) => idx !== i))} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background"><X className="h-3 w-3" /></button>
            </div>
          ))}
          {uploading ? <Spinner className="h-5 w-5 text-muted-foreground" /> : null}
        </div>
      ) : null}

      <div className="relative z-10 border-t border-border bg-background">
        <div className="mx-auto w-full max-w-2xl px-4 py-3">
          <div className={cn("flex gap-1.5 rounded-2xl border bg-secondary px-2 py-1.5 shadow-sm transition-colors", recording ? "items-center border-red-500/40" : "items-end border-border focus-within:border-ring focus-within:ring-1 focus-within:ring-ring")}>
            <input ref={imgRef} type="file" accept="image/*" multiple hidden onChange={onPickImages} data-testid="room-image-input" />
            <input ref={docRef} type="file" multiple hidden onChange={onPickDocs} data-testid="room-doc-input" />
            {recording ? (
              <VoiceRecordingBar secs={recSecs} onCancel={cancelVoice} onSend={stopVoice} cancelTestId="room-voice-cancel" sendTestId="room-voice-send" />
            ) : (
              <>
                <RoomAttachMenu
                  onImages={() => imgRef.current?.click()}
                  onCamera={() => imgRef.current?.click()}
                  onDocs={() => docRef.current?.click()}
                  maxxPresent={!!room?.maxx_present}
                  onAskMaxx={askMaxx}
                />
                <textarea data-testid="room-composer" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={room?.maxx_present ? "Message… (@maxx to ask)" : "Message…"} rows={1}
                  className="max-h-32 flex-1 resize-none bg-transparent px-1 py-2 outline-none placeholder:text-muted-foreground" />
                {text.trim() || attachments.length ? (
                  <button data-testid="room-send" onClick={send} disabled={sending} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-40"><ArrowUp className="h-5 w-5" /></button>
                ) : (
                  <button data-testid="room-voice" onClick={startVoice} className="shrink-0 self-center p-1.5"><Mic className="h-7 w-7" strokeWidth={1.6} /></button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {panel ? (
        <InfoPanel room={room} members={members} me={me} isLeader={isLeader} quizActive={!!room?.active_quiz_session_id}
          onClose={() => setPanel(false)} onInvite={() => { setPanel(false); setInviteOpen(true); }} onToggleMaxx={toggleMaxx}
          onBlock={blockUser} onRemove={removeMember} onLeave={leave}
          onReportUser={(m) => setReportFor({ ...m, member: true })} onOpenProfile={openProfile}
          onQuiz={onQuizStartClick} onLeaderboard={openLeaderboard} />
      ) : null}

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        roomName={room?.name}
        inviteUrl={inviteUrl}
        isLeader={isLeader}
        onShare={shareInvite}
        onCopy={copyInviteLink}
        onReset={resetInvite}
      />

      {profileFor ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={() => setProfileFor(null)}>
          <div data-testid="member-profile-modal" className="w-full max-w-xs rounded-t-2xl border border-border bg-background p-6 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            {profileFor.loading ? (
              <div className="flex justify-center py-10"><Spinner className="h-6 w-6 text-foreground" /></div>
            ) : (
              <div className="flex flex-col items-center text-center">
                <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-secondary text-2xl font-bold">
                  {profileFor.avatar_url ? <img src={BACKEND_ORIGIN + profileFor.avatar_url} alt="" className="h-full w-full object-cover" /> : (profileFor.name || "?")[0]?.toUpperCase()}
                </div>
                <h2 data-testid="profile-name" className="mt-3 text-xl font-semibold">{profileFor.name}{profileFor.is_me ? " (you)" : ""}</h2>
                <div className="mt-5 flex w-full justify-around">
                  <div className="flex flex-col items-center"><span data-testid="profile-xp" className="text-xl font-bold">{profileFor.total_xp}</span><span className="text-xs text-muted-foreground">XP</span></div>
                  <div className="flex flex-col items-center"><span data-testid="profile-streak" className="text-xl font-bold">🔥 {profileFor.streak}</span><span className="text-xs text-muted-foreground">Day streak</span></div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {detailFor ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={() => setDetailFor(null)}>
          <div className="w-full max-w-md rounded-t-2xl border border-border bg-background p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-semibold">Reactions</h2><button onClick={() => setDetailFor(null)}><X className="h-5 w-5" /></button></div>
            {Object.keys(detailFor.reactions || {}).filter((k) => (detailFor.reactions[k] || []).length).length === 0 ? (
              <p className="text-sm text-muted-foreground">No reactions yet.</p>
            ) : Object.entries(detailFor.reactions || {}).filter(([, u]) => u.length).map(([emo, users]) => (
              <div key={emo} data-testid={`detail-${emo}`} className="flex items-start gap-3 border-b border-border py-3 last:border-0">
                <span className="text-2xl">{emo}</span>
                <span className="pt-1 text-sm">{users.map(nameFor).join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {quizModal ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={() => setQuizModal(false)}>
          <div data-testid="quiz-start-modal" className="w-full max-w-md rounded-t-2xl border border-border bg-background p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between"><h2 className="flex items-center gap-2 text-lg font-semibold"><Zap className="h-5 w-5 text-learn" /> 21 Questions</h2><button onClick={() => setQuizModal(false)}><X className="h-5 w-5" /></button></div>
            <p className="mb-4 text-sm text-muted-foreground">MAXx builds a quiz for the whole group. Everyone answers the same questions and earns XP just for playing. Pick a topic, or leave it blank.</p>
            <Field label="Topic (optional)" value={quizTopic} onChange={(e) => setQuizTopic(e.target.value)} placeholder="e.g. fractions, WWII, photosynthesis" data-testid="quiz-topic-input" />
            <div className="mt-4">
              <div className="mb-2 text-sm font-medium text-muted-foreground">How many questions?</div>
              <div className="flex gap-2">
                {[5, 15, 21].map((n) => (
                  <button key={n} type="button" data-testid={`quiz-size-${n}`} onClick={() => setQuizCount(n)}
                    className={cn("flex-1 rounded-xl border px-3 py-2.5 text-sm font-bold transition-colors", quizCount === n ? "border-learn bg-learn/15 text-learn" : "border-border text-muted-foreground hover:text-foreground")}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <Button data-testid="quiz-start-submit" onClick={startQuiz} loading={quizBusy} className="mt-4 w-full">Start quiz</Button>
          </div>
        </div>
      ) : null}

      {leaderOpen ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={() => setLeaderOpen(false)}>
          <div data-testid="leaderboard-modal" className="w-full max-w-md rounded-t-2xl border border-border bg-background p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between"><h2 className="flex items-center gap-2 text-lg font-semibold"><Trophy className="h-5 w-5 text-learn" /> Most Active</h2><button onClick={() => setLeaderOpen(false)}><X className="h-5 w-5" /></button></div>
            {leaderboard.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No activity yet — use MAXx and play 21 Questions to climb the board!</p>
            ) : (
              <div className="flex flex-col">
                {leaderboard.map((r) => (
                  <div key={r.account_id} data-testid={`leaderboard-row-${r.account_id}`} className={cn("flex items-center gap-3 border-b border-border py-3 last:border-0", r.is_me && "font-bold")}>
                    <span className="w-7 text-center text-sm font-bold text-muted-foreground">{r.rank === 1 ? "🏆" : r.rank}</span>
                    <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-secondary text-sm font-semibold">{r.avatar_url ? <img src={BACKEND_ORIGIN + r.avatar_url} alt="" className="h-full w-full object-cover" /> : (r.name || "?")[0]?.toUpperCase()}</div>
                    <span className="flex-1 truncate">{r.name}{r.is_me ? " (you)" : ""}</span>
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      {r.streak > 0 ? <span data-testid={`leaderboard-streak-${r.account_id}`} className="font-semibold text-foreground" title={`${r.streak}-day streak`}>🔥 {r.streak}</span> : null}
                      <span>{r.total_xp} XP</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {reportFor ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={() => setReportFor(null)}>
          <div className="w-full max-w-md rounded-t-2xl border border-border bg-background p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-semibold">Report {reportFor.member ? reportFor.name : "message"}</h2><button onClick={() => setReportFor(null)}><X className="h-5 w-5" /></button></div>
            <p className="mb-4 text-sm text-muted-foreground">Reports are sent to the MAXx team for review. Pick a reason:</p>
            <div className="flex flex-col gap-2">
              {["Bullying or harassment", "Inappropriate content", "Spam", "Something else"].map((r) => (
                <button key={r} data-testid={`report-reason-${r}`} onClick={() => submitReport(r)} className="rounded-xl border border-border px-4 py-3 text-left text-sm hover:bg-accent">{r}</button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function QuizCard({ m, qstate, isCurrent, inRoster, myChoice, deadline, onAnswer, onExpire }) {
  const quiz = m.quiz || {};
  const choices = quiz.choices || [];
  const revealed = !!qstate?.revealed;
  const correctIndex = qstate?.correct_index;
  const winners = qstate?.winners || [];
  const answeredCount = qstate?.answered_count || 0;
  const rosterCount = qstate?.roster_count || 0;
  const locked = myChoice !== undefined && myChoice !== null;

  // "3…2…1…Go!" lead-in, computed from the server-stamped starts_at on the Q1 card (robust to re-renders).
  const startsAt = quiz.starts_at ? new Date(quiz.starts_at).getTime() : 0;
  const [now, setNow] = useState(Date.now());
  const counting = startsAt > 0 && !revealed && now < startsAt + 650;
  useEffect(() => {
    if (!counting) return;
    const t = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, [counting]);
  const countdownLabel = counting
    ? (startsAt - now > 0 ? String(Math.min(3, Math.max(1, Math.ceil((startsAt - now) / 1000)))) : "Go!")
    : null;
  const open = isCurrent && !revealed && !counting;

  const [secsLeft, setSecsLeft] = useState(null);
  const expiredRef = useRef(false);
  useEffect(() => {
    if (!open || !deadline) { setSecsLeft(null); return; }
    expiredRef.current = false;
    const tick = () => {
      const left = Math.max(0, Math.ceil((new Date(deadline).getTime() - Date.now()) / 1000));
      setSecsLeft(Math.min(left, 99));
      if (left <= 0 && !expiredRef.current) { expiredRef.current = true; onExpire?.(); }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [open, deadline, onExpire]);

  return (
    <div data-testid={`quiz-card-${quiz.q_index}`} className="mx-auto w-full max-w-md animate-in fade-in zoom-in-95 duration-300">
      <div className="rounded-2xl border border-primary/40 bg-primary/5 p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary"><Zap className="h-3.5 w-3.5" /> 21 Questions</span>
          <span className="text-xs font-semibold text-muted-foreground">Q{(quiz.q_index ?? 0) + 1} / {quiz.total}</span>
        </div>
        {counting ? (
          <div data-testid="quiz-countdown" className="flex flex-col items-center justify-center py-8">
            <span key={countdownLabel} className="animate-in zoom-in-50 fade-in font-display text-7xl font-black text-primary">{countdownLabel}</span>
            <span className="mt-2 text-sm font-semibold text-muted-foreground">Get ready!</span>
          </div>
        ) : (
          <>
            <h3 className="font-display text-lg font-bold leading-snug">{quiz.question}</h3>
            <div className="mt-3 flex flex-col gap-2">
              {choices.map((c, i) => {
                const isCorrect = revealed && i === correctIndex;
                const isMyWrong = revealed && i === myChoice && i !== correctIndex;
                const isMyPick = !revealed && i === myChoice;
                const clickable = open && inRoster && !locked;
                return (
                  <button
                    key={i}
                    data-testid={`quiz-card-${quiz.q_index}-choice-${i}`}
                    disabled={!clickable}
                    onClick={() => clickable && onAnswer(i)}
                    className={cn(
                      "flex items-center justify-between gap-2 rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors",
                      clickable && "border-border hover:border-primary/60 hover:bg-primary/10",
                      isMyPick && "border-primary bg-primary/15 font-semibold",
                      isCorrect && "border-green-500 bg-green-500/15 font-semibold",
                      isMyWrong && "border-red-500 bg-red-500/10",
                      !clickable && !isMyPick && !isCorrect && !isMyWrong && "border-border opacity-70",
                    )}
                  >
                    <span>{c}</span>
                    {isCorrect ? <Check className="h-4 w-4 shrink-0 text-green-500" /> : null}
                    {isMyWrong ? <X className="h-4 w-4 shrink-0 text-red-500" /> : null}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 border-t border-primary/15 pt-2.5 text-sm">
              {revealed ? (
                <div data-testid={`quiz-card-${quiz.q_index}-reveal`} className="font-semibold text-foreground">
                  {winners.length ? (
                    <span className="text-green-600 dark:text-green-400">🎉 Got it right: {winners.join(", ")}</span>
                  ) : (
                    <span className="text-muted-foreground">Nobody got that one — on to the next!</span>
                  )}
                </div>
              ) : open ? (
                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-semibold tabular-nums">
                    <Clock className="h-3.5 w-3.5" /> {secsLeft !== null ? `${secsLeft}s` : "…"}
                  </span>
                  <span data-testid={`quiz-card-${quiz.q_index}-progress`} className="tabular-nums">
                    {locked ? "Locked in · " : !inRoster ? "You're watching · " : ""}{answeredCount}/{rosterCount} answered
                  </span>
                </div>
              ) : (
                <div className="text-muted-foreground">Waiting…</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RoomAttachMenu({ onImages, onCamera, onDocs, maxxPresent, onAskMaxx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid="room-attach-btn" onClick={() => setOpen((o) => !o)} className="p-1.5"><Plus className="h-6 w-6" strokeWidth={1.5} /></button>
      {open ? (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full left-0 z-30 mb-2 w-48 rounded-xl border border-border bg-popover p-1 shadow-lg">
            <button data-testid="room-attach-image" onClick={() => { setOpen(false); onImages(); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent">Photo Library</button>
            <button data-testid="room-attach-camera" onClick={() => { setOpen(false); onCamera(); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent">Take Photo</button>
            <button data-testid="room-attach-file" onClick={() => { setOpen(false); onDocs(); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent">Files</button>
            {maxxPresent ? (
              <button data-testid="ask-maxx-btn" onClick={() => { setOpen(false); onAskMaxx(); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent"><Sparkles className="h-4 w-4" /> Ask MAXx</button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}


function Attachments({ list, mediaToken }) {
  if (!list?.length) return null;
  return (
    <div className="mb-1.5 flex flex-wrap gap-1.5">
      {list.map((a, i) => a.type === "image" ? (
        <a key={i} href={fileUrl(a.storage_path, mediaToken)} target="_blank" rel="noreferrer" data-testid={`attach-image-${i}`}>
          <img src={fileUrl(a.storage_path, mediaToken)} alt={a.name || ""} className="h-40 w-40 rounded-xl object-cover" />
        </a>
      ) : (
        <a key={i} href={fileUrl(a.storage_path, mediaToken)} target="_blank" rel="noreferrer" data-testid={`attach-file-${i}`} className="flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm">
          <FileText className="h-4 w-4 text-muted-foreground" /> {a.name || "file"}
        </a>
      ))}
    </div>
  );
}

function Reactions({ m, me, onDetails }) {
  const rx = m.reactions || {};
  const keys = Object.keys(rx).filter((k) => (rx[k] || []).length);
  if (!keys.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {keys.map((emo) => {
        const users = rx[emo] || [];
        return (
          <button key={emo} data-testid={`reaction-${m.id}-${emo}`} onClick={onDetails}
            className={cn("flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs", users.includes(me) ? "border-foreground bg-secondary" : "border-border")}>
            <span>{emo}</span><span className="tabular-nums">{users.length}</span>
          </button>
        );
      })}
    </div>
  );
}

function RoomBubble({ m, me, mediaToken, playingId, onReport, onReact, onDetails, onPlay, onOpenProfile }) {
  const [pick, setPick] = useState(false);
  if (m.kind === "system") {
    if ((m.text || "").includes("MAXx joined")) {
      return (
        <div className="my-2 flex justify-center animate-in fade-in zoom-in-95 duration-300">
          <span data-testid="maxx-join-banner" className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> MAXx joined the chat 👋
          </span>
        </div>
      );
    }
    return <div className="my-1 text-center text-xs text-muted-foreground">{m.text}</div>;
  }
  const isMaxx = m.kind === "maxx";
  const mine = m.sender_id === me;
  const attach = m.attachments || [];
  const isVoice = m.via_voice;
  const bubbleCls = mine ? "bg-primary text-primary-foreground" : isMaxx ? "border border-primary/40 bg-primary/10 text-foreground" : "bg-secondary text-foreground";
  return (
    <div className={cn("flex flex-col animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-300 ease-out", mine ? "items-end" : "items-start")}>
      {!mine ? (isMaxx ? (
        <span className="mb-0.5 flex items-center gap-1 px-1 text-xs font-semibold text-primary"><Sparkles className="h-3 w-3" />MAXx</span>
      ) : (
        <button data-testid={`peer-name-${m.id}`} onClick={() => onOpenProfile?.(m.sender_id)} className="mb-0.5 px-1 text-xs font-semibold text-muted-foreground hover:underline">{m.sender_name}</button>
      )) : null}
      <Attachments list={attach} mediaToken={mediaToken} />
      <div className={cn("group flex items-end gap-1.5", mine ? "flex-row-reverse" : "flex-row")}>
        {!mine && !isMaxx ? (
          <button data-testid={`peer-avatar-${m.id}`} onClick={() => onOpenProfile?.(m.sender_id)} className="mb-0.5 flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary text-[10px] font-bold">
            {m.sender_avatar ? <img src={BACKEND_ORIGIN + m.sender_avatar} alt="" className="h-full w-full object-cover" /> : (m.sender_name || "?")[0]?.toUpperCase()}
          </button>
        ) : null}
        {m.text ? (
          <div
            data-testid={mine ? "my-bubble" : isMaxx ? "maxx-bubble" : "peer-bubble"}
            onClick={isVoice ? onPlay : undefined}
            className={cn("max-w-[78vw] whitespace-pre-wrap rounded-2xl px-4 py-2.5 leading-relaxed sm:max-w-md", bubbleCls, isVoice && "cursor-pointer")}
          >
            {isVoice ? (
              <span className="mb-1 flex items-center gap-1.5 text-xs font-semibold opacity-80" data-testid={`voice-play-${m.id}`}>
                {playingId === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Voice note · tap to play
              </span>
            ) : null}
            {clean(m.text)}
          </div>
        ) : null}
        <div className="relative flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          <button data-testid={`react-btn-${m.id}`} onClick={() => setPick((o) => !o)} className="mb-1 p-1 text-muted-foreground"><SmilePlus className="h-3.5 w-3.5" /></button>
          {!mine && !isMaxx ? <button data-testid={`report-msg-${m.id}`} onClick={onReport} className="mb-1 p-1 text-muted-foreground"><Flag className="h-3.5 w-3.5" /></button> : null}
          {pick ? (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setPick(false)} />
              <div className="absolute bottom-full z-40 mb-1 flex gap-1 rounded-full border border-border bg-popover p-1 shadow-lg">
                {EMOJIS.map((e) => (
                  <button key={e} data-testid={`react-${m.id}-${e}`} onClick={() => { onReact(e); setPick(false); }} className="rounded-full px-1.5 py-0.5 text-base hover:bg-accent">{e}</button>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>
      <Reactions m={m} me={me} onDetails={onDetails} />
    </div>
  );
}

function InviteModal({ open, onClose, roomName, inviteUrl, isLeader, onShare, onCopy, onReset }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose}>
      <div data-testid="invite-modal" className="w-full max-w-md rounded-t-2xl border border-border bg-background p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Invite to {roomName}</h2>
          <button data-testid="invite-close" onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">Anyone with this link can join the chat. Share it with your friends.</p>

        <div className="mb-4 flex items-center gap-2 rounded-xl border border-border bg-secondary px-3 py-3">
          <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input data-testid="invite-link-value" readOnly value={inviteUrl} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
        </div>

        <div className="flex flex-col gap-2">
          <Button data-testid="invite-share-btn" onClick={onShare}><Share2 className="h-4 w-4" /> Share link</Button>
          <Button variant="secondary" data-testid="invite-copy-btn" onClick={onCopy}><Copy className="h-4 w-4" /> Copy link</Button>
          {isLeader ? (
            <button data-testid="invite-reset-btn" onClick={onReset} className="mt-1 flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground">
              <RotateCw className="h-4 w-4" /> Reset link (stops the old one working)
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function InfoPanel({ room, members, me, isLeader, quizActive, onClose, onInvite, onToggleMaxx, onBlock, onRemove, onLeave, onReportUser, onQuiz, onLeaderboard, onOpenProfile }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div className="absolute right-0 top-0 flex h-full w-full max-w-sm flex-col border-l border-border bg-background" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-border px-4 py-3"><h2 className="text-lg font-semibold">Chat info</h2><button data-testid="info-close" onClick={onClose}><X className="h-5 w-5" /></button></header>
        <div className="flex-1 overflow-auto p-4">
          <div className="mb-2 text-xl font-bold">{room?.name}</div>
          <button data-testid="invite-friends-btn" onClick={onInvite} className="mb-5 flex w-full items-center justify-between rounded-xl border border-primary bg-primary/5 px-4 py-3 text-left text-sm font-semibold text-primary"><span className="flex items-center gap-2"><Share2 className="h-4 w-4" /> Invite friends</span></button>
          <button data-testid="toggle-maxx" onClick={onToggleMaxx} className="mb-3 flex w-full items-center justify-between rounded-xl border border-border px-4 py-3 text-left text-sm"><span className="flex items-center gap-2"><Sparkles className="h-4 w-4" /> {room?.maxx_present ? "Remove MAXx from chat" : "Add MAXx to chat"}</span></button>
          <button data-testid="room-quiz-btn" onClick={onQuiz} className="mb-3 flex w-full items-center justify-between rounded-xl border border-border px-4 py-3 text-left text-sm"><span className="flex items-center gap-2"><Zap className="h-4 w-4" /> {quizActive ? "21 Questions in play" : "Start 21 Questions"}</span></button>
          <button data-testid="room-leaderboard-btn" onClick={onLeaderboard} className="mb-5 flex w-full items-center justify-between rounded-xl border border-border px-4 py-3 text-left text-sm"><span className="flex items-center gap-2"><Trophy className="h-4 w-4" /> Most Active</span></button>
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Members ({members.length})</div>
          <div className="flex flex-col">
            {members.map((mem) => {
              const isMe = mem.account_id === me;
              return (
                <div key={mem.account_id} data-testid={`member-${mem.account_id}`} className="flex items-center gap-2 border-b border-border py-3">
                  <button data-testid={`member-open-${mem.account_id}`} onClick={() => onOpenProfile?.(mem.account_id)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                    <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-secondary text-sm font-semibold">{mem.avatar_url ? <img src={BACKEND_ORIGIN + mem.avatar_url} alt="" className="h-full w-full object-cover" /> : (mem.name || "?")[0]?.toUpperCase()}</div>
                    <span className="flex min-w-0 items-center gap-1.5 truncate font-medium">{mem.name}{isMe ? " (you)" : ""}{mem.role === "leader" ? <Crown className="h-3.5 w-3.5 text-muted-foreground" /> : null}</span>
                  </button>
                  {!isMe ? (
                    <div className="flex items-center gap-1">
                      <button data-testid={`report-user-${mem.account_id}`} onClick={() => onReportUser(mem)} className="p-1.5 text-muted-foreground" title="Report"><Flag className="h-4 w-4" /></button>
                      <button data-testid={`block-user-${mem.account_id}`} onClick={() => onBlock(mem)} className="p-1.5 text-muted-foreground" title="Block"><Ban className="h-4 w-4" /></button>
                      {isLeader ? <button data-testid={`remove-member-${mem.account_id}`} onClick={() => onRemove(mem)} className="p-1.5 text-muted-foreground" title="Remove"><UserMinus className="h-4 w-4" /></button> : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
        <div className="border-t border-border p-4"><Button variant="secondary" data-testid="leave-chat" onClick={onLeave} className="w-full"><LogOut className="h-4 w-4" /> Leave chat</Button></div>
      </div>
    </div>
  );
}
